import BootstrapTable from 'react-bootstrap-table-next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { sortCaret, sizePerPageRenderer, paginationTotalRenderer } from '../../../utils/tableHelper';
import Button from '../../common/Button';
import Spinner from '../../common/Spinner';
import StatusBadge from '../../common/StatusBadge';
import ReturnProductModal from '../../modals/ReturnProductModal';
import SupplierOrderSummaryModal from '../../modals/SupplierOrderSummaryModal';
import SupplierOrderFulfillModal from '../../modals/SupplierOrderFulfillModal';
import RetailerImage from '../../common/RetailerImage';
import { dateFormatter, orderTypeFormatter } from '../../../utils/formatHelper';
import { getOrderDocuments } from '../../../utils/orderHelper';
import { ORDER_STATE_PROCESSING, ORDER_STATE_CSR, ORDER_STATE_COMPLETED } from '../../../constants/orderStates';
import { areAllItemsReturned } from '../../../utils/fulfillmentHelper';
import { missingDataPlaceholder } from '../../../constants/missingDataPlaceholder';
import { brandOrdersDownloadDocuments } from '../../../store/actions/brandOrders';

import styles from './BrandOrdersTable.module.scss';
import { supplierRetailerUtils } from '../../../utils/supplierRetailer';
import { getSupplierRetailersFromStateSelector } from '../../../utils/stateHelper';

function BrandOrdersTable(props) {
  const { order, perPage, orderLoading, data } = props;
  const dispatch = useDispatch();
  const { downloadOrderDocuments } = useSelector((state) => state.brandOrders);
  const supplierRetailers = useSelector(getSupplierRetailersFromStateSelector);

  const [selected, setSelected] = useState([]);
  const [filesToDownload, setFilesToDownload] = useState([]);
  const [modals, setModals] = useState({
    return: false,
    summary: false,
    fulfill: false,
  });
  const [additionalOrderStates, setAdditionalOrderStates] = useState({
    displayStatus: null,
    infoStatus: null,
    postShipmentStatus: null,
  });

  const selectAllRef = useRef(null);

  useEffect(() => {
    const indeterminate = selected.length > 0 && selected.length < data.length;
    selectAllRef.current.indeterminate = indeterminate;
  }, [selected, data]);

  const downloadDocuments = (orders) => {
    dispatch(brandOrdersDownloadDocuments(orders));
  };

  const openModal = (e, type, orderId) => {
    if (orderId) {
      props.getOrder(orderId);
    }
    if (type === 'summary') {
      const specificOrder = data.find((order) => order.id === orderId);
      const {
        display_status: displayStatus,
        additional_info_status: infoStatus,
        post_shipment_status: postShipmentStatus,
      } = specificOrder;
      setAdditionalOrderStates({ displayStatus, infoStatus, postShipmentStatus });
    }
    setModals((prevModals) => ({
      ...prevModals,
      [type]: true,
    }));

    e.preventDefault();
    e.stopPropagation();
  };

  const closeModals = () => {
    setModals({
      return: false,
      summary: false,
      fulfill: false,
    });
  };

  const canFulfill = (state) => state === ORDER_STATE_PROCESSING || state === ORDER_STATE_CSR;
  const isStateCompleted = (state) => state === ORDER_STATE_COMPLETED;
  const canDownloadDocuments = (row) => Object.keys(getOrderDocuments(row)).length > 0;

  const openFulfillModal = (e, row) => {
    if (canFulfill(row.processing_state)) {
      openModal(e, 'fulfill', row.id);
    }
    e.stopPropagation();
    e.preventDefault();
  };

  const openReturnModal = (e, row) => {
    if (isStateCompleted(row.processing_state) && !areAllItemsReturned(row)) {
      openModal(e, 'return', row.id);
    }
    e.stopPropagation();
    e.preventDefault();
  };

  const openDocumentsDownload = (e, row) => {
    if (canDownloadDocuments(row)) {
      downloadDocuments([row]);
    }
    e.stopPropagation();
    e.preventDefault();
  };

  const tooltipAppearance = (row) => {
    return isStateCompleted(row.processing_state) && areAllItemsReturned(row) ? (
      <Tooltip id={row.id}>
        <h5>All items have been returned</h5>
      </Tooltip>
    ) : (
      <></>
    );
  };
  const formatActions = (cell, row) => {
    return (
      <div className={styles.actions}>
        <button
          onClick={(e) => openFulfillModal(e, row)}
          className={canFulfill(row.processing_state) ? '' : styles.disabled}
        >
          Ship
        </button>

        <OverlayTrigger key={row.id} placement="top" overlay={tooltipAppearance(row)}>
          <button
            onClick={(e) => openReturnModal(e, row)}
            className={isStateCompleted(row.processing_state) && !areAllItemsReturned(row) ? '' : styles.disabled}
          >
            Return
          </button>
        </OverlayTrigger>

        <button
          onClick={(e) => openDocumentsDownload(e, row)}
          className={canDownloadDocuments(row) ? '' : styles.disabled}
        >
          Documents
        </button>
      </div>
    );
  };

  const columns = [
    {
      text: 'id',
      dataField: 'id',
      hidden: true,
    },
    {
      text: 'Sales channel',
      dataField: 'retailer_name',
      formatter(cell, row) {
        const retailerName = supplierRetailerUtils.getRetailerDisplayNameFromRetailerName({
          supplierRetailers,
          retailerName: cell,
        });

        return (
          <div className={styles.retailerImage}>
            <RetailerImage image={row.retailer_logo} size="40" name={cell} inline />
            {retailerName}
          </div>
        );
      },
      align: 'left',
    },
    {
      text: 'Type',
      dataField: 'type',
      formatter: (cell) => <span>{orderTypeFormatter(cell)}</span>,
      headerStyle: { width: '12%' },
    },
    {
      text: 'Date',
      dataField: 'created_at',
      formatter: dateFormatter,
      sort: true,
      headerStyle: { width: '12%' },
    },
    {
      text: '',
      dataField: 'retailer_order_id',
      headerClasses: 'filtered',
      filter: textFilter({
        placeholder: 'Order Id/ Sales Order Number',
        delay: 300,
        defaultValue: props.search,
      }),
    },
    {
      text: 'Shipment status',
      dataField: 'processing_state',
      formatter: (cell, row) => (
        <StatusBadge
          status={row.display_status}
          id={`brand_processing_state_${row.id}`}
          className={styles.status}
          simplify
        />
      ),
      headerStyle: { width: '12%' },
    },
    {
      text: 'Info',
      dataField: 'info',
      formatter: (cell, row) =>
        row.additional_info_status.length ? (
          <StatusBadge
            status={row.additional_info_status}
            id={`brand_info_state_${row.id}`}
            className={styles.status}
            simplify
            additionalInfo
          />
        ) : (
          missingDataPlaceholder
        ),
      headerStyle: { width: '12%' },
    },
    {
      text: 'Post-Shipment Status',
      dataField: 'post_shipment_state',
      formatter: (cell, row) =>
        row.post_shipment_status ? (
          <StatusBadge
            status={row.post_shipment_status}
            id={`brand_post_shipment_state_${row.id}`}
            className={styles.status}
            simplify
            postShipmentStatus
          />
        ) : (
          missingDataPlaceholder
        ),
      headerStyle: { width: '12%' },
    },
    {
      text: 'Actions',
      dataField: 'actions',
      isDummyField: true,
      formatter: formatActions,
      classes: styles.actionsCol,
    },
  ];

  const selectRowOptions = {
    mode: 'checkbox',
    clickToSelect: true,

    onSelect: (row, isSelected, rowIndex, event) => {
      const isSelectionCell =
        event.target.classList.contains('selection-cell') ||
        event.target.parentNode.classList.contains('selection-cell');

      if (isSelectionCell) {
        if (selected.includes(row.id)) {
          setSelected(selected.filter((x) => x !== row.id));
          setFilesToDownload(canDownloadDocuments(row) ? filesToDownload.filter((x) => x !== row.id) : filesToDownload);
        } else {
          setSelected([...selected, row.id]);
          setFilesToDownload((prevFilesToDownload) =>
            canDownloadDocuments(row) ? [...prevFilesToDownload, row.id] : prevFilesToDownload,
          );
        }
      } else {
        openModal(event, 'summary', row.id);
        event.preventDefault();
        event.stopPropagation();
      }
    },

    onSelectAll: (isSelected, rows, event) => {
      const ids = data.map((order) => order.id);
      if (selected.length === ids.length && selected.length > 0) {
        setSelected([]);
        setFilesToDownload([]);
      } else {
        setSelected(ids);
        setFilesToDownload(data.filter((order) => canDownloadDocuments(order)).map((order) => order.id));
      }

      event.preventDefault();
      event.stopPropagation();
    },

    selectionRenderer: (selectionProps) => {
      const isSelected = selected.includes(selectionProps.rowKey);
      return <input type="checkbox" checked={isSelected} onChange={(e) => {}} />;
    },

    selectionHeaderRenderer: (selectionProps) => {
      const isSelected = selected.length === data.length && data.length > 0;
      return <input type="checkbox" checked={isSelected} onChange={(e) => {}} ref={selectAllRef} />;
    },
  };

  const rowClasses = (row, rowIndex) => {
    if (row.processing_state !== ORDER_STATE_COMPLETED) {
      return styles.hasSummaryOrder;
    }
  };

  const noDataIndication = () => (props.isLoading ? <Spinner size="small" /> : `There are no orders to display`);

  const onTableChange = (type, newState) => {
    switch (type) {
      case 'sort':
        if (props.sort !== newState.sortOrder) {
          props.setSort(newState.sortOrder);
        }
        break;

      case 'filter':
        let searchValue = '';
        const orderIdFilter = newState.filters.retailer_order_id;
        if (orderIdFilter) {
          searchValue = orderIdFilter.filterVal;
        }

        if (props.search !== searchValue) {
          props.setOffset(0);
          props.filterById(searchValue);
        }
        break;

      default:
        break;
    }
  };

  const onPageChange = (page) => props.setOffset((page - 1) * perPage);

  const pagination = paginationFactory({
    page: props.offset / perPage + 1,
    sizePerPage: perPage,
    sizePerPageList: [10, 25, 50],
    onPageChange,
    onSizePerPageChange: props.setPerPage,
    totalSize: props.statusCount,
    showTotal: true,
    paginationTotalRenderer,
    sizePerPageRenderer: (sizePerPageProps) => {
      return (
        <div className={styles.sizePerPageContainer}>
          <Button
            text="Download Documents"
            onClick={downloadSelectedOrders}
            disabled={!filesToDownload.length || !!downloadOrderDocuments}
          />
          {sizePerPageRenderer(sizePerPageProps)}
        </div>
      );
    },
  });

  const downloadSelectedOrders = () => {
    const orders = filesToDownload.map((orderId) => data.find((order) => order.id === orderId));
    dispatch(brandOrdersDownloadDocuments(orders));
  };

  const isAutoCancelledOrder = order?.fulfillments?.length === 0;

  return (
    <>
      {!orderLoading && !isAutoCancelledOrder && (
        <ReturnProductModal
          open={modals.return}
          onClose={closeModals}
          data={order}
          postReturns={props.postReturns}
          returnReasons={props.returnReasons}
        />
      )}

      {!orderLoading && (
        <SupplierOrderSummaryModal
          opened={modals.summary}
          order={order}
          onClose={closeModals}
          onRefresh={() => props.getOrder(order.id)}
          cancelFulfillments={props.cancelFulfillments}
          additionalStates={additionalOrderStates}
        />
      )}

      {!orderLoading && (
        <SupplierOrderFulfillModal
          opened={modals.fulfill}
          order={order}
          supplierId={props.supplierId}
          onClose={closeModals}
          submit={props.fulfillOrder}
        />
      )}

      <div className={styles.container}>
        <BootstrapTable
          data={props.isLoading ? [] : data}
          columns={columns}
          keyField="id"
          striped
          hover
          bordered={false}
          className={styles.container}
          selectRow={selectRowOptions}
          rowClasses={rowClasses}
          noDataIndication={noDataIndication}
          filter={filterFactory()}
          pagination={pagination}
          onTableChange={onTableChange}
          remote={{
            filter: true,
            pagination: true,
            sort: true,
          }}
          sort={{
            sortCaret: sortCaret,
          }}
          defaultSorted={[
            {
              dataField: 'created_at',
              order: props.sort,
            },
          ]}
        ></BootstrapTable>
      </div>
    </>
  );
}

export default BrandOrdersTable;

BrandOrdersTable.defaultProps = {
  statusCount: 0,
};

BrandOrdersTable.propTypes = {
  postReturns: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  setOffset: PropTypes.func.isRequired,
  filterById: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  getOrder: PropTypes.func.isRequired,
  offset: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  setSort: PropTypes.func.isRequired,
  sort: PropTypes.string.isRequired,
  statusCount: PropTypes.number,
  cancelFulfillments: PropTypes.func.isRequired,
  fulfillOrder: PropTypes.func.isRequired,
};
